import { Coins, Zap } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatCreditsForDisplay } from '../../../utils/creditsSync';

export interface CreditsPillProps {
  credits: number | null;
  creditsLoading: boolean;
  onUpgradeClick: () => void;
}

/** Compact credits badge for the workspace top bar. Pulses amber when low. */
const CreditsPill: React.FC<CreditsPillProps> = ({
  credits,
  creditsLoading,
  onUpgradeClick,
}) => {
  const { t } = useTranslation();
  const count = credits ?? 0;
  const display = formatCreditsForDisplay(count, { loading: creditsLoading });
  const isLow = !creditsLoading && count <= 2;
  const isEmpty = !creditsLoading && count <= 0;
  const title = creditsLoading
    ? t('shell.credits.loading', 'Loading credits...')
    : t('shell.credits.remaining', { defaultValue: '{{count}} credits remaining', count });
  const label = isEmpty
    ? t('shell.credits.getMore', 'Get credits')
    : t('shell.credits.label', 'credits');

  return (
    <button
      type="button"
      onClick={onUpgradeClick}
      title={title}
      className={[
        'inline-flex h-7 min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-all hover:scale-105 active:scale-95',
        isEmpty
          ? 'border-red-400/40 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-300 animate-pulse'
          : isLow
            ? 'border-amber-400/40 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-900/30 dark:text-amber-300'
            : 'border-emerald-400/30 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-900/20 dark:text-emerald-300',
      ].join(' ')}
      aria-label={t('shell.credits.aria', { defaultValue: '{{count}} credits', count })}
    >
      {isEmpty ? (
        <Zap className="h-3.5 w-3.5 shrink-0" aria-hidden />
      ) : (
        <Coins className="h-3.5 w-3.5 shrink-0" aria-hidden />
      )}
      <span className="font-bold tabular-nums leading-none">
        {display}
      </span>
      <span className="hidden sm:inline text-[10px] opacity-70">{label}</span>
    </button>
  );
};

export default CreditsPill;
